import { TrackUserAction } from './track-user-actions.service';
import { EntityType } from '@shared/models/entity-enum';
import { DataObject } from '@shared/models/data-object.model';
import { TimSections } from '@miq/catalog';
import { RecordDetailContext } from './details-page/format-helpers';
import { getPropertyMarketByUsage } from './details-page/utils';

export enum TrackUserActionID {
  TemplafyReportDownload = 192,
  MoreLink = 193,
  LeaseCloneAndCreateNew = 194,
  LeaseAddTenancy = 195,
  LeaseViewTenancy = 196,
  LeaseAssociateTim = 197,
  LeaseDelete = 198,
  LeaseLinkAvail = 199,
  LeaseUnlinkAvail = 200,
  LeaseVerify = 201,
  AvailCloneAndCreateNew = 217,
  AvailLinkLease = 218,
  AvailUnlinkLease = 219,
  AvailDelete = 220,
  AvailVerify = 221,
  AvailMoreTakeOffMkt = 222,
  AvailTakeOffMkt = 223,
  TimCloneAndCreateNew = 253,
  TimDelete = 254,
  TimVerify = 255,
  PropCloneAndCreateNew = 233,
  PropAddAvail = 234,
  PropAddLease = 235,
  PropAddSale = 236,
  PropAddTenancy = 237,
  PropDelete = 238,
  PropVerify = 239,
  SelectComplex = 256,
  ExternalRatings = 257,
  AddCompany = 258,
  DeleteCompany = 259,
  CreateCorporateReport = 265,
  DownloadCorporateReport = 266,
}

export interface IMoreActions {
  cloneAndCreateNew: IMoreActionTrackingObj;
  link?: IMoreActionTrackingObj;
  unlink?: IMoreActionTrackingObj;
  delete: IMoreActionTrackingObj;
  verify: IMoreActionTrackingObj;
  associate?: IMoreActionTrackingObj;
  addTenancy?: IMoreActionTrackingObj;
  associateTim?: IMoreActionTrackingObj;
  takeOffMkt?: IMoreActionTrackingObj;
  viewTenancy?: IMoreActionTrackingObj;
  addAvail?: IMoreActionTrackingObj;
  addLeaseComp?: IMoreActionTrackingObj;
  addSaleComp?: IMoreActionTrackingObj;
}

export interface IMoreActionTrackingObj {
  actionPage: string;
  actionTaken: string;
  actionId: number;
}

export interface IMorePropertyTrackingObj extends IMoreActionTrackingObj {
  propertyId: number;
  propertyMarket: string;
}

export interface IMoreTimTrackingObj extends IMoreActionTrackingObj {
  timId: number;
  targetMarkets: Array<string>;
}

export interface IMoreAvailTrackingObj extends IMoreActionTrackingObj {
  listingType: any;
  availabilityId: number;
}

export enum QUEUES_TABS_NAME {
  AvailabilityCalc = 'availabilityCalc',
  Property = 'property',
  Company = 'company',
  Contact = 'contact',
  Lease = 'lease',
  Sale = 'sale',
  Availability = 'availability',
  Tim = 'tim',
}

export enum QueueActionsId {
  queuesAvailabilityCalcVisited = 202,
  queuesPropertyVisited = 203,
  queuesCompanyVisited = 204,
  queuesContactVisited = 205,
  queuesLeaseVisited = 206,
  queuesSaleVisited = 207,
  queuesAvailabilityVisited = 208,
  queuesTIMVisited = 209,
}

export const QueuesActions: { [key: string]: TrackUserAction | any } = {
  visitPage: {
    [QUEUES_TABS_NAME.AvailabilityCalc]: { actionId: QueueActionsId.queuesAvailabilityCalcVisited },
    [QUEUES_TABS_NAME.Lease]: { actionId: QueueActionsId.queuesLeaseVisited },
    [QUEUES_TABS_NAME.Tim]: { actionId: QueueActionsId.queuesTIMVisited },
    [QUEUES_TABS_NAME.Property]: { actionId: QueueActionsId.queuesPropertyVisited },
    [QUEUES_TABS_NAME.Availability]: { actionId: QueueActionsId.queuesAvailabilityVisited },
    [QUEUES_TABS_NAME.Sale]: { actionId: QueueActionsId.queuesSaleVisited },
    [QUEUES_TABS_NAME.Company]: { actionId: QueueActionsId.queuesCompanyVisited },
    [QUEUES_TABS_NAME.Contact]: { actionId: QueueActionsId.queuesContactVisited },
  },
};

export enum SearchCompanyActionIds {
  addCompany = 210,
  addCompanyAndContact = 211,
  search = 212,
  closeModal = 213,
  create = 214,
}

export enum ListingRepUpdatesActionIds {
  closeModal = 215,
  listRepUpdate = 216,
}

export enum DataStackActionIds {
  navigateToDataStack = 286,
}

export function isEntityTracked(entityTracked: EntityType): boolean {
  return [EntityType.Availability, EntityType.LeaseComp].includes(entityTracked);
}

export function extractTIMTrackingActionItems(
  record: DataObject,
  actionId: number,
  actionName: string
): TrackUserAction {
  const timId = record?.entity?.id ?? 0;
  const ligGeo = record?.target_geography?.lig_geography ?? [];
  return {
    actionId,
    actionDetails: {
      entity: EntityType.Tim,
      entityId: timId,
      entityData: { targetMarkets: ligGeo.map(({ market_desc }) => market_desc) },
      actionName,
    },
  };
}

export interface TrackingFuncsBasedOnSectionId {
  add: (record: Record<string, any>) => TrackUserAction;
  delete: (record: Record<string, any>) => TrackUserAction;
  edit?: (record: Record<string, any>) => TrackUserAction;
}
export const trackUserActionBasedOnSectionId: { [key: number]: TrackingFuncsBasedOnSectionId } = {
  [TimSections.TargetPropertyType]: {
    add: record => extractTIMTrackingActionItems(record, 241, 'Add Target Property Type'),
    delete: record => extractTIMTrackingActionItems(record, 242, 'Delete Target Property Type'),
    edit: record => extractTIMTrackingActionItems(record, 243, 'Edit Target Property Type'),
  },
  [TimSections.TargetLocations]: {
    add: record => extractTIMTrackingActionItems(record, 244, 'Add Target Location'),
    delete: record => extractTIMTrackingActionItems(record, 245, 'Delete Target Location'),
    edit: record => extractTIMTrackingActionItems(record, 246, 'Edit Target Location'),
  },
  [TimSections.CurrentLocations]: {
    add: record => extractTIMTrackingActionItems(record, 247, 'Add Current Location'),
    delete: record => extractTIMTrackingActionItems(record, 248, 'Delete Current Location'),
    edit: record => extractTIMTrackingActionItems(record, 249, 'Edit Current Location'),
  },
  [TimSections.Companies]: {
    add: record => extractTIMTrackingActionItems(record, 250, 'Add Companies'),
    delete: record => extractTIMTrackingActionItems(record, 251, 'Delete Companies'),
  },
};

export function getCompanyTrackingPayload(context: RecordDetailContext, actionId: TrackUserActionID) {
  const { recordState, dataState } = context;
  const entity = recordState.type;
  const actionPage = `${entity.charAt(0).toUpperCase()}${entity.slice(1)} Detail Page`;
  const propertyMarket = getPropertyMarketByUsage(dataState.record, recordState.selectedUsage);

  return {
    actionId,
    actionsDetails: {
      entity,
      actionPage,
      entityId: recordState.id,
      entityData: {
        propertyMarket,
      },
    },
  };
}
